/**
 * City configuration for SEO landing pages. Each city gets its own page at
 * /kurer-{slug} optimized for "kurer [city]" Google searches.
 */
export const CITIES = {
  "koebenhavn": {
    slug: "koebenhavn",
    name: "København",
    nameLong: "København",
    zip: "1050",
    headline: "Kurer i København",
    tagline: "Direkte kurérservice i hele København — same-day, express og aftenlevering.",
    intro:
      "EMA Kurer er din lokale kurér i København. Vi henter pakken direkte hos dig og kører den direkte til modtageren — ingen lager, ingen omlastning, ingen ventetid. Same-day, express og aftenlevering i hele hovedstadsområdet.",
    placeholderFrom: "Storgade 5, 1050 København K",
    placeholderTo: "Strøget 12, 1160 København K",
    deliveryZones: ["1050 København K", "2100 København Ø", "2300 København S", "2400 København NV", "2500 Valby", "2700 Brønshøj", "2200 København N"],
    nearby: ["frederiksberg", "lyngby", "roskilde", "koege"],
    useCases: [
      { t: "Webshops i København", d: "Bestil før 18 — leveret samme aften til kunder i hovedstaden." },
      { t: "Advokater & revisorer", d: "Bekræftede dokument-leveringer med foto-bevis og live tracking." },
      { t: "Restauranter & catering", d: "Aften-leverancer af mad, drikke og udstyr på tværs af København." },
      { t: "Klinikker & apotek", d: "Diskrete leveringer af recepter, prøver og medicinsk udstyr." },
    ],
    faq: [
      { q: "Hvad koster en kurer i København?", a: "Same-Day Kurér starter fra 199 kr inkl. 8 km. Express fra 289 kr og Aftenlevering fra 249 kr. Ekstra km koster 6 kr/km, så en typisk levering inden for København koster fra 199 til 250 kr." },
      { q: "Hvor hurtigt kan I hente i København?", a: "Vi henter typisk indenfor 60-90 minutter efter booking ved Same-Day og indenfor 30-60 minutter ved Express. Du kan se realistisk afhentningstid før du booker." },
      { q: "Leverer I om aftenen i København?", a: "Ja. Aftenlevering 18:00-22:00 er ideel hvis modtageren først er hjemme om aftenen — fra 249 kr inkl. 8 km." },
      { q: "Hvilke områder af København dækker I?", a: "Vi dækker hele 1000- og 2000-serien af postnumre i København samt Frederiksberg, Amager og hele Sjælland." },
    ],
  },
  "roskilde": {
    slug: "roskilde",
    name: "Roskilde",
    nameLong: "Roskilde",
    zip: "4000",
    headline: "Kurer i Roskilde",
    tagline: "Direkte kurérservice i Roskilde og omegn. Bestil online, vi henter samme dag.",
    intro:
      "EMA Kurer er din lokale kurérpartner i Roskilde. Vi kører direkte mellem A og B — uden lager eller omlastning. Same-day, express og aftenlevering til både privatkunder og virksomheder i hele Roskilde-området.",
    placeholderFrom: "Algade 31, 4000 Roskilde",
    placeholderTo: "Havnen 8, 4000 Roskilde",
    deliveryZones: ["4000 Roskilde", "4030 Tune", "4040 Jyllinge", "4050 Skibby", "4070 Kirke Hyllinge"],
    nearby: ["koebenhavn", "koege", "hilleroed"],
    useCases: [
      { t: "Webshops i Roskilde", d: "Same-day levering til kunder i Roskilde og Sjælland." },
      { t: "Advokater & revisorer", d: "Sikre dokument-leveringer med foto-bevis." },
      { t: "Restauranter", d: "Aften-leverancer til private og firmaer." },
      { t: "Klinikker", d: "Diskret transport af medicinsk udstyr og prøver." },
    ],
    faq: [
      { q: "Hvad koster en kurer i Roskilde?", a: "Same-Day Kurér fra 199 kr inkl. 8 km. Lokal levering i Roskilde-området koster typisk 199-220 kr." },
      { q: "Kan I køre fra Roskilde til København?", a: "Ja. Roskilde-København er en typisk rute for os. En levering på ~33 km koster ca. 349 kr Same-Day." },
      { q: "Hvor hurtigt kan I hente i Roskilde?", a: "Typisk 60-90 minutter efter booking ved Same-Day. Express er endnu hurtigere." },
    ],
  },
  "koege": {
    slug: "koege",
    name: "Køge",
    nameLong: "Køge",
    zip: "4600",
    headline: "Kurer i Køge",
    tagline: "Direkte kurérservice i Køge og omegn — same-day og aftenlevering.",
    intro:
      "Brug for hurtig kurérlevering i Køge? EMA Kurer henter pakken hos dig og kører den direkte til modtageren. Ingen lager, ingen omlastning. Vi leverer i hele Køge-området og hele Sjælland.",
    placeholderFrom: "Torvet 1, 4600 Køge",
    placeholderTo: "Søndre Allé 35, 4600 Køge",
    deliveryZones: ["4600 Køge", "4623 Lille Skensved", "4140 Borup", "4632 Bjæverskov"],
    nearby: ["koebenhavn", "roskilde", "naestved"],
    useCases: [
      { t: "Erhvervskurer i Køge", d: "Faste kuréraftaler for lokale virksomheder med månedsfaktura." },
      { t: "Same-day til København", d: "Bestil før 18 — leveret samme aften i hovedstaden." },
      { t: "Webshops i Køge", d: "Forøg konvertering med 'Bestil før 18, leveret i aften'-løfte." },
    ],
    faq: [
      { q: "Hvad koster en kurer i Køge?", a: "Same-Day Kurér fra 199 kr inkl. 8 km. Lokal levering i Køge typisk 199-230 kr." },
      { q: "Kører I fra Køge til København?", a: "Ja, det er en standardrute. Køge-København koster ca. 350 kr Same-Day (~40 km)." },
    ],
  },
  "helsingoer": {
    slug: "helsingoer",
    name: "Helsingør",
    nameLong: "Helsingør",
    zip: "3000",
    headline: "Kurer i Helsingør",
    tagline: "Direkte kurérservice i Helsingør og Nordsjælland.",
    intro:
      "EMA Kurer leverer professionel kurérservice i Helsingør og det øvrige Nordsjælland. Vi henter pakken direkte hos dig og kører den direkte til modtageren — uden lager, uden omlastning.",
    placeholderFrom: "Stengade 11, 3000 Helsingør",
    placeholderTo: "Sct. Anna Gade 5, 3000 Helsingør",
    deliveryZones: ["3000 Helsingør", "3060 Espergærde", "3070 Snekkersten", "3140 Ålsgårde"],
    nearby: ["hilleroed", "koebenhavn"],
    useCases: [
      { t: "Same-day til København", d: "Helsingør-København på 50 km — leveret samme aften." },
      { t: "Aftenlevering i Nordsjælland", d: "Ideel når modtageren først er hjemme efter arbejde." },
      { t: "Erhvervskurer", d: "Faste aftaler for nordsjællandske virksomheder." },
    ],
    faq: [
      { q: "Hvad koster en kurer i Helsingør?", a: "Same-Day Kurér fra 199 kr inkl. 8 km. Lokal levering i Helsingør typisk 199-220 kr." },
      { q: "Kører I fra Helsingør til København?", a: "Ja. ~50 km, koster ca. 451 kr Same-Day." },
    ],
  },
  "hilleroed": {
    slug: "hilleroed",
    name: "Hillerød",
    nameLong: "Hillerød",
    zip: "3400",
    headline: "Kurer i Hillerød",
    tagline: "Direkte kurérservice i Hillerød og Nordsjælland.",
    intro:
      "EMA Kurer kører direkte fra A til B i Hillerød og omegn — uden mellemstationer. Same-day, express og aftenlevering for både privatkunder og virksomheder i Nordsjælland.",
    placeholderFrom: "Slotsgade 1, 3400 Hillerød",
    placeholderTo: "Helsingørsgade 25, 3400 Hillerød",
    deliveryZones: ["3400 Hillerød", "3450 Allerød", "3460 Birkerød", "3480 Fredensborg"],
    nearby: ["helsingoer", "koebenhavn", "roskilde"],
    useCases: [
      { t: "Erhverv i Hillerød", d: "Faste kuréraftaler med dedikeret kontaktperson." },
      { t: "Same-day til København", d: "Hillerød-København på ~40 km, leveret samme dag." },
      { t: "Klinikker & laboratorier", d: "Diskret transport af prøver og medicinsk udstyr." },
    ],
    faq: [
      { q: "Hvad koster en kurer i Hillerød?", a: "Same-Day Kurér fra 199 kr inkl. 8 km. Lokal levering 199-230 kr." },
    ],
  },
  "naestved": {
    slug: "naestved",
    name: "Næstved",
    nameLong: "Næstved",
    zip: "4700",
    headline: "Kurer i Næstved",
    tagline: "Direkte kurérservice i Næstved og Sydsjælland.",
    intro:
      "EMA Kurer er din lokale kurér i Næstved. Vi henter pakken direkte hos dig og kører den direkte til modtageren i Sydsjælland eller resten af Sjælland — uden lager, uden omlastning.",
    placeholderFrom: "Axeltorv 1, 4700 Næstved",
    placeholderTo: "Ringstedgade 35, 4700 Næstved",
    deliveryZones: ["4700 Næstved", "4720 Præstø", "4730 Glumsø", "4760 Vordingborg"],
    nearby: ["koege", "roskilde"],
    useCases: [
      { t: "Erhverv i Næstved", d: "Faste kuréraftaler for sydsjællandske virksomheder." },
      { t: "Webshops i Næstved", d: "Same-day levering til kunder i hele Sjælland." },
      { t: "Privatkunder", d: "Lokal kurér der kender området." },
    ],
    faq: [
      { q: "Hvad koster en kurer i Næstved?", a: "Same-Day Kurér fra 199 kr inkl. 8 km. Lokal levering 199-230 kr." },
    ],
  },
  "slagelse": {
    slug: "slagelse",
    name: "Slagelse",
    nameLong: "Slagelse",
    zip: "4200",
    headline: "Kurer i Slagelse",
    tagline: "Direkte kurérservice i Slagelse og Vestsjælland.",
    intro:
      "EMA Kurer leverer professionel kurérservice i Slagelse og hele Vestsjælland. Vi henter direkte hos dig og kører direkte til modtageren — same-day, express og aftenlevering, uden lager eller omlastning.",
    placeholderFrom: "Stenstuegade 5, 4200 Slagelse",
    placeholderTo: "Jernbanegade 3, 4200 Slagelse",
    deliveryZones: ["4200 Slagelse", "4220 Korsør", "4230 Skælskør", "4243 Rude", "4250 Fuglebjerg"],
    nearby: ["naestved", "holbaek", "ringsted"],
    useCases: [
      { t: "Erhverv i Slagelse", d: "Faste kuréraftaler for vestsjællandske virksomheder med månedsfaktura." },
      { t: "Same-day til København", d: "Slagelse-København på ~85 km — leveret samme dag." },
      { t: "Webshops & e-handel", d: "Bestil før 18 — leveret samme aften i hele området." },
    ],
    faq: [
      { q: "Hvad koster en kurer i Slagelse?", a: "Same-Day Kurér fra 199 kr inkl. 8 km. Lokal levering i Slagelse-området typisk 199-250 kr." },
      { q: "Kører I fra Slagelse til København?", a: "Ja. Slagelse-København er ~85 km og koster ca. 661 kr Same-Day." },
    ],
  },
  "holbaek": {
    slug: "holbaek",
    name: "Holbæk",
    nameLong: "Holbæk",
    zip: "4300",
    headline: "Kurer i Holbæk",
    tagline: "Direkte kurérservice i Holbæk og det nordvestlige Sjælland.",
    intro:
      "EMA Kurer er din lokale kurérpartner i Holbæk. Vi kører direkte mellem A og B i hele Holbæk-området og resten af Sjælland — uden lager, uden omlastning, uden ventetid.",
    placeholderFrom: "Ahlgade 14, 4300 Holbæk",
    placeholderTo: "Smedelundsgade 25, 4300 Holbæk",
    deliveryZones: ["4300 Holbæk", "4340 Tølløse", "4360 Kirke Eskilstrup", "4390 Vipperød"],
    nearby: ["slagelse", "roskilde", "kalundborg"],
    useCases: [
      { t: "Erhverv i Holbæk", d: "Faste kuréraftaler med dedikeret kontaktperson." },
      { t: "Same-day til København", d: "Holbæk-København på ~65 km, leveret samme dag." },
      { t: "Webshops i Holbæk", d: "Same-day levering forøger din konvertering." },
    ],
    faq: [
      { q: "Hvad koster en kurer i Holbæk?", a: "Same-Day Kurér fra 199 kr inkl. 8 km. Lokal levering typisk 199-240 kr." },
    ],
  },
  "kalundborg": {
    slug: "kalundborg",
    name: "Kalundborg",
    nameLong: "Kalundborg",
    zip: "4400",
    headline: "Kurer i Kalundborg",
    tagline: "Direkte kurérservice i Kalundborg og Vestsjælland.",
    intro:
      "EMA Kurer leverer same-day kurérservice i Kalundborg og hele det vestlige Sjælland. Vi kører direkte mellem afhentning og levering — ingen lager, ingen omlastning, ingen ventetid.",
    placeholderFrom: "Kordilgade 1, 4400 Kalundborg",
    placeholderTo: "Klosterstræde 10, 4400 Kalundborg",
    deliveryZones: ["4400 Kalundborg", "4470 Svebølle", "4480 Store Fuglede", "4490 Jerslev Sjælland"],
    nearby: ["holbaek", "slagelse"],
    useCases: [
      { t: "Industri & havn", d: "Hurtig levering til/fra Kalundborg Havn og virksomhederne i området." },
      { t: "Same-day til København", d: "Kalundborg-København på ~105 km, leveret samme dag." },
      { t: "Erhvervskunder", d: "Faste aftaler med månedlig fakturering." },
    ],
    faq: [
      { q: "Hvad koster en kurer i Kalundborg?", a: "Same-Day Kurér fra 199 kr inkl. 8 km. Lokal levering typisk 199-260 kr." },
    ],
  },
  "ringsted": {
    slug: "ringsted",
    name: "Ringsted",
    nameLong: "Ringsted",
    zip: "4100",
    headline: "Kurer i Ringsted",
    tagline: "Direkte kurérservice i Ringsted — centralt på Sjælland.",
    intro:
      "EMA Kurer kører direkte fra A til B i Ringsted og hele midten af Sjælland. Med Ringsteds centrale placering er vi ofte hurtigt fremme — same-day, express og aftenlevering.",
    placeholderFrom: "Sct. Bendtsgade 1, 4100 Ringsted",
    placeholderTo: "Nørregade 17, 4100 Ringsted",
    deliveryZones: ["4100 Ringsted", "4130 Viby Sjælland", "4174 Jystrup", "4190 Munke Bjergby"],
    nearby: ["roskilde", "koege", "slagelse", "naestved"],
    useCases: [
      { t: "Erhverv i Ringsted", d: "Centralt placeret — vi når både København, Køge og Slagelse hurtigt." },
      { t: "Distribution", d: "Same-day distribution fra dit lager til kunder i hele Sjælland." },
      { t: "Webshops", d: "Bestil før 18 — leveret samme aften." },
    ],
    faq: [
      { q: "Hvad koster en kurer i Ringsted?", a: "Same-Day Kurér fra 199 kr inkl. 8 km. Lokal levering 199-230 kr." },
    ],
  },
  "frederikssund": {
    slug: "frederikssund",
    name: "Frederikssund",
    nameLong: "Frederikssund",
    zip: "3600",
    headline: "Kurer i Frederikssund",
    tagline: "Direkte kurérservice i Frederikssund og Nordsjælland.",
    intro:
      "EMA Kurer leverer hurtig kurérservice i Frederikssund og det øvrige Nordsjælland. Vi henter pakken hos dig og kører den direkte til modtageren — same-day og express, uden mellemstationer.",
    placeholderFrom: "Jernbanegade 13, 3600 Frederikssund",
    placeholderTo: "Bruhnsvej 1, 3600 Frederikssund",
    deliveryZones: ["3600 Frederikssund", "3630 Jægerspris", "3650 Ølstykke", "3670 Veksø Sjælland"],
    nearby: ["hilleroed", "koebenhavn", "roskilde"],
    useCases: [
      { t: "Same-day til København", d: "Frederikssund-København på ~40 km, leveret samme dag." },
      { t: "Erhverv i Nordsjælland", d: "Faste aftaler for nordsjællandske virksomheder." },
      { t: "Privatkunder", d: "Lokal kurér der kender området." },
    ],
    faq: [
      { q: "Hvad koster en kurer i Frederikssund?", a: "Same-Day Kurér fra 199 kr inkl. 8 km. Lokal levering 199-220 kr." },
    ],
  },
  "nykoebing-f": {
    slug: "nykoebing-f",
    name: "Nykøbing F",
    nameLong: "Nykøbing Falster",
    zip: "4800",
    headline: "Kurer i Nykøbing Falster",
    tagline: "Direkte kurérservice i Nykøbing F og Sydsjælland/Falster.",
    intro:
      "EMA Kurer leverer kurérservice til Nykøbing Falster og hele Sydsjælland samt Lolland-Falster. Vi kører direkte fra A til B — same-day og express på lange ruter, planlæg gerne i forvejen.",
    placeholderFrom: "Jernbanegade 5, 4800 Nykøbing F",
    placeholderTo: "Slotsgade 1, 4800 Nykøbing F",
    deliveryZones: ["4800 Nykøbing F", "4862 Guldborg L", "4863 Eskilstrup", "4880 Nysted"],
    nearby: ["naestved"],
    useCases: [
      { t: "Erhverv i Nykøbing F", d: "Faste kuréraftaler for virksomheder i Sydsjælland og Falster." },
      { t: "Lange ruter", d: "Vi kører gerne lange ture — bestil i god tid for bedste pris." },
      { t: "Klinikker & laboratorier", d: "Transport af prøver og medicinsk udstyr." },
    ],
    faq: [
      { q: "Kører I helt til Nykøbing Falster?", a: "Ja. Det er en lang rute (~150 km fra København), så bestil gerne i god tid. Tag-kontakt for præcis pris." },
    ],
  },
};

export const CITY_LIST = Object.values(CITIES);
